/**
 * The index-clone checkpoint doc (SUP-314): a `Project` doc cloned from the
 * live index at capture, with a `FileVersion` on every in-scope file inode
 * and one `checkpoint` block. Written by `capture.ts` in exactly one change
 * after the clone, and never changed again.
 *
 * This module is the pure half: the shape, its guard, and the projection to
 * the `{ path, heads, structure }` content the existing restore and history
 * consumers read. `vcs.ts:loadCheckpointContent` is the one adapter that
 * calls it.
 */
import { isInlineDir, resolveInode } from "../fs/paths";
import type { FileVersion, PlayDirInode, PlayFileInode } from "../fs/types";
import type { CheckpointContent, Project } from "../project";
import { filesUnderScope, normalizeDirPath } from "./scope";

export interface IndexCloneMeta {
  kind: "index-clone";
  /** Directory the checkpoint scopes (`"/"` = whole project). */
  scope: string;
  /** Epoch ms when the versions were resolved and the clone stamped. */
  capturedAt: number;
  /** The live index this was cloned from, and its heads at capture. */
  source: { indexUrl: string; heads: string[] };
  /** In-scope file inodes, every one of them versioned. */
  fileCount: number;
  /** Always true on a published clone: an unstamped clone has no block. */
  complete: true;
  /**
   * A linked tree (`fs/dir-tree.ts`): the version of every in-scope entry
   * the clone doesn't hold (files reached through a linked directory, and the
   * directories themselves), by uri. The clone holds only the project
   * document's own entries, which carry their stamps as ever.
   */
  versions?: Record<string, FileVersion>;
  /** The urls in `versions` that are linked directories. */
  directories?: string[];
  /** A scope inside a linked directory: its inode, which the clone can't
   *  resolve (it lives in that directory's document). */
  scopeInode?: PlayFileInode;
}

export type IndexCloneDoc = Project & { checkpoint: IndexCloneMeta };

export function isIndexCloneDoc(doc: unknown): doc is IndexCloneDoc {
  if (typeof doc !== "object" || doc === null) return false;
  const meta = (doc as { checkpoint?: unknown }).checkpoint;
  return (
    typeof meta === "object" &&
    meta !== null &&
    (meta as IndexCloneMeta).kind === "index-clone" &&
    (meta as IndexCloneMeta).complete === true &&
    "files" in doc
  );
}

/**
 * Project a clone onto the legacy content shape. `structure` is the clone's
 * own file tree at the scope (its inodes keep their `version`); `heads` is
 * the per-url map the restore path consumes, read off those versions.
 */
export function cloneCheckpointContent(doc: IndexCloneDoc): CheckpointContent {
  const dir = normalizeDirPath(doc.checkpoint.scope);
  const { versions, directories, scopeInode } = doc.checkpoint;
  let structure: CheckpointContent["structure"];
  if (dir === "/") structure = doc.files;
  else structure = scopeInode ?? resolveInode(doc.files, dir) ?? null;
  // The heads map is a walk over the whole scope, and a file read never
  // needs it (the inode carries its version), so it is built on first use:
  // restore and GC ask, single-file reads don't pay.
  let heads: Record<string, string[]> | undefined;
  return {
    path: dir,
    structure,
    ...(directories && { directories: [...directories] }),
    ...(versions && { versions }),
    get heads() {
      heads ??= { ...headsOfClone(doc.files, dir), ...headsOf(versions) };
      return heads;
    },
  };
}

function headsOf(
  versions: Record<string, FileVersion> | undefined,
): Record<string, string[]> {
  const heads: Record<string, string[]> = {};
  for (const [uri, version] of Object.entries(versions ?? {})) {
    if (version.kind === "automerge") heads[uri] = [...version.heads];
  }
  return heads;
}

function headsOfClone(
  files: Record<string, PlayFileInode>,
  dir: string,
): Record<string, string[]> {
  const heads: Record<string, string[]> = {};
  for (const { inode } of filesUnderScope(files, dir)) {
    const version = inode.version;
    if (version?.kind === "automerge" && !heads[inode.uri]) {
      heads[inode.uri] = [...version.heads];
    }
  }
  return heads;
}

/** A plain deep copy of `inode` with every `version` dropped, its own and
 *  its descendants': the shape live inodes have. */
export function withoutVersion(inode: PlayFileInode): PlayFileInode {
  const { version: _version, ...rest } = inode;
  if (!isInlineDir(inode)) return rest;
  const dir: PlayDirInode = {
    ...rest,
    files: {
      schemaVersion: inode.files.schemaVersion,
      entries: withoutVersions(inode.files.entries),
    },
  };
  return dir;
}

/** {@link withoutVersion} over an entries map. */
export function withoutVersions(
  entries: Record<string, PlayFileInode>,
): Record<string, PlayFileInode> {
  return Object.fromEntries(
    Object.entries(entries).map(([name, inode]) => [
      name,
      withoutVersion(inode),
    ]),
  );
}
